/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RequestStatus } from '../../shared/enums/request-status';
import {
  UserSetting,
  ProfileProgress,
  UserSettingCode,
} from '../../shared/types/user-setting';
import { FeatureUsage } from '../../shared/types/feature-usage';
import { Sequence } from '../sequence/types';
import {
  getFirstSequenceRequest,
  getSampleSequenceRequest,
  getUserSettingsRequest,
  updateUserSettingsRequest,
  upgradeOnboardingStepRequest,
  getUserNotificationsRequest,
  getUserUnreadNotificationsRequest,
  getUnreadEmailThreadsCountRequest,
  markNotificationsAsReadRequest,
  createSequenceFromGettingStartedRequest,
  compareAuthTokenRequest,
  getAgencyConfigRequest,
  getPostLoadMetaRequest,
  extendTrialPeriodRequest,
  getCoreFeaturesCreditsRequest,
  getPollingNotificationsRequest,
  updateHiddenNotificationRequest,
  exploreDemoAccountRequest,
  getUserSubscriptionMetadataRequest,
  updateUserFeatureEngagementRequest,
  updateLeadFinderVersionRequest,
} from './extra-actions';
import updateProfileProgressRequest from '../../shared/components/getting-started-onboarding/extra-actions';
import {
  AgencyConfig,
  GetPostLoadMetaResponse,
  NotificationPollingResponse,
  Notifications,
  PaymentAction,
  SubscriptionDetails,
  UpdateUserFeatureEngagementPayload,
} from './types';
import { IcpStatus } from '../../shared/enums/icp-status';
import { defaultAgencyObj } from './utils/helper';
import { SSOStatus } from '../../shared/enums/sso-status';

type RequestState = {
  status: RequestStatus;
  message: string;
  error: any;
};

type ApplicationState = {
  showActivateSequenceTooltip?: boolean;
  showAddEmailAccountModal?: boolean;
  hasUserConnectedEmailAccount?: boolean;
  loggedUserEmail?: string;
  shouldLogoutUser?: boolean;
  logoURL?: string;
  hasSignUpCompleted?: boolean;
  helpscoutBeaconId?: string;
  hasVisitedAgencyPortal?: boolean;
  showDemoFeedbackModal?: boolean;
};

type AdminDetails = {
  email: string;
  firstName: string;
  lastName: string;
};

interface Credit {
  featureCode: string;
  featureLimits?: string; // Optional, since not all features have a limit
  accountUsageQuotaRemaining: string;
}

type State = {
  getUserSettingsRequest: RequestState;
  getUserSubscriptionMetadataRequest: RequestState;
  getAgencyConfigRequest: RequestState;
  getFirstSequenceRequest: RequestState;
  getUserNotificationsRequest: RequestState;
  getPollingNotificationsRequest: RequestState;
  updateHiddenNotificationRequest: RequestState;
  getSampleSequenceRequest: RequestState;
  updateUserSettingsRequest: RequestState;
  upgradeOnboardingStepRequest: RequestState;
  updateProfileProgressRequest: RequestState;
  createSequenceFromGettingStartedRequest: RequestState;
  getUserUnreadNotificationsRequest: RequestState;
  getUnreadEmailThreadsCountRequest: RequestState;
  compareAuthTokenRequest: RequestState;
  getPostLoadMetaRequest: RequestState;
  extendTrialPeriodRequest: RequestState;
  getCoreFeaturesCreditsRequest: RequestState;
  updateUserFeatureEngagementRequest: RequestState;
  updateLeadFinderVersionRequest: RequestState;
  credits: Credit[];
  meta: UserSetting[];
  profileProgress: ProfileProgress[];
  gettingStatedCreatedSequenceId: number;
  email: string;
  firstName: string;
  lastName: string;
  trackingId: string;
  firstSequence: Sequence;
  team_size: string;
  subscription: SubscriptionDetails;
  leadFinderSubscription: SubscriptionDetails;
  inboxAnalyzerSubscription: SubscriptionDetails;
  dialerSubscription: SubscriptionDetails;
  isDialerPlanPurchasedPreviously: boolean;
  subscriptionMetadata: PaymentAction[];
  notifications: Notifications[];
  notificationCount: number;
  pollingNotifications: NotificationPollingResponse;
  blinkNotificationIcon: boolean;
  role: string;
  client: any;
  featureUsages: FeatureUsage[];
  permissions: string[];
  resources: string[];
  designation: string;
  signup_date: string;
  markNotificationsAsReadRequest: RequestState;
  application: ApplicationState;
  adminDetails: AdminDetails;
  churnkeyHash: string;
  leadFinderChurnkeyHash: string;
  inboxAnalyzerChurnkeyHash: string;
  agencyConfig: AgencyConfig;
  unreadNotificationCount: number;
  unreadEmailThreadsCount: number;
  filteredUnreadEmailThreadsCount: number;
  showYoutubeVideModal: boolean;
  icpStatus: IcpStatus;
  getPostLoadMetaResponse: GetPostLoadMetaResponse;
  createdAt: Date;
  isEligibleToExtendTrial: boolean;
  allowInboxRotation: boolean;
  userId: number;
  planName: string;
  useCase: string;
  industry: string;
  companySize: string;
  jobRole: string;
  country: string;
  phoneNumber: string;
  referralPage: string;
  landingPage: string;
  shAccountId: number;
  verified: boolean;
  timeZone: string;
  atmRole: string;
  isAgencyUser: boolean;
  isMultichannelActive: boolean;
  isSaleshandyCopilotEnabled: boolean;
  leadFinderLimit: number;
  isAgency: string;
  isSelfSignup: boolean;
  shouldBulkActionsPoll: boolean;
  firebaseProgressNotifications: Record<string, any>;
  hasDomains: boolean;
  hasExpiringOrders: boolean;
  hasDedicatedIps: boolean;
  intercomHash: string;
  initiateFetchUnreadCount: boolean;
  ssoStatus: SSOStatus;
  organizationId: string;
  shouldDisplayMobileAppBanner: boolean;
  isFirstStepTextOnlyEmail: boolean;
  customTrackingDomainEnabled: boolean;
  is1To1EmailInsightsEnabled: boolean;
  exploreDemoAccountRequest: RequestState;
  demoAccountAccessToken: string;
  isDemoAccount: boolean;
  creditGiven: boolean;
  isPlanUpgradeScheduled: boolean;
  demoAccountEntryPoint: string;
  isLtdUser: boolean;
  isProspectEnrichmentEnabled: boolean;
  shouldShowChangePassword: boolean; // this is used for the Oauth users only
  isProspectEnrollmentEnabled?: boolean;
  hasV2LeadFinderAccess?: boolean;
  defaultEmailRevealType?: string;
  dataExportPermission?: number;
  leadFinder: {
    hasLeadFinderV2Access: boolean;
    version: 'v1' | 'v2' | null;
    isVersionLocked: boolean;
    isFilterRestrictionEnabled?: number;
    isBucket1Frozen?: number;
    restrictedV1Access?: boolean;
    csvEnrichment?: {
      row?: number;
      column?: number;
      size?: number; // in kb
    };
    credits?: {
      company: number;
      email: number;
      emailphone: number;
    };
  };
  updateUserFeatureEngagementResponse: UpdateUserFeatureEngagementPayload;
  paymentProvider: string;
  isDialerLocalPresenceEnabled: boolean;
  isDialerFeatureEnabled: boolean;
};

const initialState: State = {
  getAgencyConfigRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  markNotificationsAsReadRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getUserSettingsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getUserSubscriptionMetadataRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getFirstSequenceRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getUserNotificationsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getPollingNotificationsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  updateHiddenNotificationRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getSampleSequenceRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  updateUserSettingsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  upgradeOnboardingStepRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  updateProfileProgressRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  createSequenceFromGettingStartedRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getUserUnreadNotificationsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getUnreadEmailThreadsCountRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  compareAuthTokenRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getPostLoadMetaRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  extendTrialPeriodRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getCoreFeaturesCreditsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  updateUserFeatureEngagementRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  updateLeadFinderVersionRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  credits: [],
  meta: null,
  profileProgress: null,
  email: null,
  firstName: null,
  planName: null,
  lastName: null,
  trackingId: null,
  firstSequence: null,
  subscription: null,
  leadFinderSubscription: null,
  inboxAnalyzerSubscription: null,
  dialerSubscription: null,
  isDialerPlanPurchasedPreviously: false,
  subscriptionMetadata: [],
  notifications: [],
  notificationCount: 0,
  pollingNotifications: null,
  blinkNotificationIcon: false,
  team_size: null,
  designation: null,
  role: null,
  client: null,
  useCase: null,
  industry: null,
  country: null,
  phoneNumber: null,
  referralPage: null,
  shAccountId: null,
  application: {
    showActivateSequenceTooltip: false,
    showAddEmailAccountModal: false,
    hasUserConnectedEmailAccount: false,
    loggedUserEmail: '',
    shouldLogoutUser: false,
    logoURL: '',
    hasSignUpCompleted: false,
    hasVisitedAgencyPortal: false,
    showDemoFeedbackModal: false,
  },
  featureUsages: [],
  permissions: [],
  resources: [],

  signup_date: null,
  gettingStatedCreatedSequenceId: null,
  adminDetails: null,
  churnkeyHash: null,
  leadFinderChurnkeyHash: null,
  inboxAnalyzerChurnkeyHash: null,
  agencyConfig: defaultAgencyObj,
  unreadNotificationCount: null,
  unreadEmailThreadsCount: null,
  filteredUnreadEmailThreadsCount: 0,
  showYoutubeVideModal: null,
  icpStatus: null,
  getPostLoadMetaResponse: null,
  createdAt: null,
  isEligibleToExtendTrial: false,
  allowInboxRotation: false,
  userId: null,
  companySize: null,
  jobRole: null,
  landingPage: null,
  verified: true,
  timeZone: null,
  atmRole: '',
  isAgencyUser: false,
  isMultichannelActive: false,
  isSaleshandyCopilotEnabled: false,
  leadFinderLimit: 0,
  isAgency: '0',
  isSelfSignup: true,
  shouldBulkActionsPoll: true,
  firebaseProgressNotifications: {},
  hasDomains: false,
  hasExpiringOrders: false,
  hasDedicatedIps: false,
  intercomHash: null,
  initiateFetchUnreadCount: false,
  ssoStatus: null,
  organizationId: null,
  shouldDisplayMobileAppBanner: false,
  isFirstStepTextOnlyEmail: false,
  customTrackingDomainEnabled: false,
  is1To1EmailInsightsEnabled: false,
  isProspectEnrollmentEnabled: false,
  hasV2LeadFinderAccess: false,
  defaultEmailRevealType: 'work_email',
  dataExportPermission: 1,
  leadFinder: {
    hasLeadFinderV2Access: false,
    version: null,
    isVersionLocked: false,
  },
  exploreDemoAccountRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  demoAccountAccessToken: null,
  isDemoAccount: false,
  creditGiven: false,
  isPlanUpgradeScheduled: false,
  demoAccountEntryPoint: '',
  isLtdUser: false,
  shouldShowChangePassword: false,
  updateUserFeatureEngagementResponse: null,
  paymentProvider: null,
  isDialerLocalPresenceEnabled: false,
  isDialerFeatureEnabled: false,
};

type ReadNotification = {
  notificationId?: string;
};

const homeSlice = createSlice({
  name: 'meta',
  initialState,
  reducers: {
    updateIsPlanUpgradeScheduled: (state) => {
      state.isPlanUpgradeScheduled = true;
    },
    markNotificationAsRead: (
      state,
      action: PayloadAction<ReadNotification>,
    ) => {
      const { notificationId } = action.payload;

      if (notificationId) {
        state.notifications = state.notifications.map((notification) => {
          // eslint-disable-next-line no-underscore-dangle
          if (notification._id === notificationId) {
            notification.hasRead = true;
          }
          return notification;
        });
      } else {
        state.notifications = state.notifications.map((notification) => ({
          ...notification,
          hasRead: true,
        }));
        if (state?.pollingNotifications?.notifications?.length > 0) {
          state.pollingNotifications.notifications = state.pollingNotifications.notifications.map(
            (notification) => ({ ...notification, hasRead: true }),
          );
        }

        state.unreadNotificationCount = null;
      }
    },
    resetCoreFeaturesCreditsRequestStatus: (state) => {
      state.getCoreFeaturesCreditsRequest.status =
        initialState.getCoreFeaturesCreditsRequest.status;
      state.getCoreFeaturesCreditsRequest.message =
        initialState.getCoreFeaturesCreditsRequest.message;
      state.getCoreFeaturesCreditsRequest.error =
        initialState.getCoreFeaturesCreditsRequest.error;
    },
    updateApplicationState: (
      state,
      action: PayloadAction<ApplicationState>,
    ) => {
      state.application = { ...state.application, ...action.payload };
    },
    updateIsFirstStepTextOnlyEmail: (state) => {
      state.isFirstStepTextOnlyEmail = !state.isFirstStepTextOnlyEmail;
    },
    setDefaultEmailRevealType: (state, action: PayloadAction<string>) => {
      state.defaultEmailRevealType = action.payload;
    },
    setCustomTrackingDomainEnabled: (state, action: PayloadAction<boolean>) => {
      state.customTrackingDomainEnabled = action.payload;
    },
    updateSkipOnboardingValue: (state) => {
      state.meta =
        state.meta?.map((obj) =>
          obj.code === UserSettingCode.SkipOnboarding
            ? { ...obj, value: '1' }
            : obj,
        ) || [];
    },
    updateShowSidebarValue: (state) => {
      state.meta =
        state.meta?.map((obj) =>
          obj.code === UserSettingCode.ShowSidebar
            ? { ...obj, value: '0' }
            : obj,
        ) || [];
    },

    showGettingStartedVideoModal: (
      state,
      action: PayloadAction<{ show: boolean }>,
    ) => {
      state.showYoutubeVideModal = action.payload.show;
    },
    resetFirstSequenceData: (state) => {
      state.firstSequence = null;
    },
    resetGetUserSubscriptionMetadataRequest: (state) => {
      state.getUserSubscriptionMetadataRequest =
        initialState.getUserSubscriptionMetadataRequest;
    },
    resetCompareAuthTokenRequest: (state) => {
      state.createSequenceFromGettingStartedRequest =
        initialState.compareAuthTokenRequest;
    },
    resetApplicationState: (state) => {
      state.application.hasSignUpCompleted = false;
    },
    resetNotificationData: (state) => {
      state.notifications = initialState.notifications;
    },
    onSettingsTabProfileUpdate: (state, action) => {
      state.firstName = action.payload?.firstName || '';
      state.lastName = action.payload?.lastName || '';
      state.email = action.payload?.email || '';
      state.timeZone = action.payload?.timeZone || '';
    },
    updateIsMultichannelActive: (state) => {
      state.isMultichannelActive = true;
    },
    setIsDialerLocalPresenceEnabled: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isDialerLocalPresenceEnabled = action.payload;
    },
    enableBulkActionRequestPolling: (state) => {
      state.shouldBulkActionsPoll = true;
    },
    disableBulkActionRequestPolling: (state) => {
      state.shouldBulkActionsPoll = false;
    },
    setFirebaseProgressNotification: (
      state,
      action: PayloadAction<{ uuid: string; notification: any }>,
    ) => {
      state.firebaseProgressNotifications[action.payload.uuid] =
        action.payload.notification;
    },
    clearFirebaseProgressNotification: (
      state,
      action: PayloadAction<string>,
    ) => {
      delete state.firebaseProgressNotifications[action.payload];
    },
    startNotificationBlink: (state) => {
      state.blinkNotificationIcon = true;
    },
    stopNotificationBlink: (state) => {
      state.blinkNotificationIcon = false;
    },
    resetInitiateFetchUnreadCount: (state) => {
      state.initiateFetchUnreadCount = false;
    },
    triggerFetchUnreadCount: (state) => {
      state.initiateFetchUnreadCount = true;
    },
    updateShowMobileApplicationBannerInMeta: (state) => {
      state.shouldDisplayMobileAppBanner = false;
    },
    deductAiVariantCredit: (state) => {
      const aiVariantCredit =
        state.getPostLoadMetaResponse.accountUsageQuotaRemaining[
          'AI_VARIANT.CREDIT'
        ];

      if (aiVariantCredit) {
        state.getPostLoadMetaResponse.accountUsageQuotaRemaining[
          'AI_VARIANT.CREDIT'
        ] = (Number(aiVariantCredit) - 1).toString();
      }
    },
    resetExploreDemoAccountRequest: (state) => {
      state.exploreDemoAccountRequest = initialState.exploreDemoAccountRequest;
    },
    setDemoAccountEntryPoint: (state, action) => {
      state.demoAccountEntryPoint = action.payload;
    },
    updateLeadFinder: (
      state,
      action: PayloadAction<{
        hasLeadFinderV2Access?: boolean;
        version?: 'v1' | 'v2' | null;
        isVersionLocked?: boolean;
      }>,
    ) => {
      state.leadFinder = { ...state.leadFinder, ...action.payload };
    },
    resetUpdateLeadFinderVersionRequest: (state) => {
      state.updateLeadFinderVersionRequest =
        initialState.updateLeadFinderVersionRequest;
    },
  },
  extraReducers: (builder) => {
    // Get Color Palette And Logo
    builder.addCase(getAgencyConfigRequest.pending, (state) => {
      state.getAgencyConfigRequest.status = RequestStatus.Pending;
      state.getAgencyConfigRequest.error = null;
    });
    builder.addCase(getAgencyConfigRequest.fulfilled, (state, action) => {
      state.getAgencyConfigRequest.status = RequestStatus.Succeeded;
      state.getAgencyConfigRequest.message = action.payload.message;
      state.agencyConfig = action.payload.payload;
    });
    builder.addCase(getAgencyConfigRequest.rejected, (state, action) => {
      state.getAgencyConfigRequest.status = RequestStatus.Failed;
      state.getAgencyConfigRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get User Subscription Metadata
    builder.addCase(getUserSubscriptionMetadataRequest.pending, (state) => {
      state.getUserSubscriptionMetadataRequest.status = RequestStatus.Pending;
      state.getUserSubscriptionMetadataRequest.error = null;
    });
    builder.addCase(
      getUserSubscriptionMetadataRequest.fulfilled,
      (state, action) => {
        state.getUserSubscriptionMetadataRequest.status =
          RequestStatus.Succeeded;
        state.getUserSubscriptionMetadataRequest.message =
          action.payload.message;
        state.subscriptionMetadata = action.payload.payload;
      },
    );
    builder.addCase(
      getUserSubscriptionMetadataRequest.rejected,
      (state, action) => {
        state.getUserSubscriptionMetadataRequest.status = RequestStatus.Failed;
        state.getUserSubscriptionMetadataRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Get user settings
    builder.addCase(getUserSettingsRequest.pending, (state) => {
      state.getUserSettingsRequest.status = RequestStatus.Pending;
      state.getUserSettingsRequest.error = null;
    });
    builder.addCase(getUserSettingsRequest.fulfilled, (state, action) => {
      state.getUserSettingsRequest.status = RequestStatus.Succeeded;
      state.getUserSettingsRequest.message = action.payload.message;
      state.meta = action.payload.payload.meta
        ? action.payload.payload.meta
        : [];
      state.profileProgress = action.payload.payload.profileProgress;
      state.subscription = action.payload.payload.account?.subscription;
      state.leadFinderSubscription =
        action.payload.payload.account?.leadFinderSubscription;
      state.inboxAnalyzerSubscription =
        action.payload.payload.account?.inboxAnalyzerSubscription;
      state.dialerSubscription =
        action.payload.payload.account?.dialerSubscription;
      state.isDialerPlanPurchasedPreviously =
        action.payload.payload?.isDialerPlanPurchasedPreviously;
      state.firstName = action.payload.payload.firstName;
      state.lastName = action.payload.payload.lastName;
      state.trackingId = action.payload.payload.trackingId;
      state.email = action.payload.payload.email;
      state.role = action.payload.payload.role;
      state.client = action.payload.payload?.client || {};
      state.churnkeyHash = action.payload.payload.churnkeyHash;
      state.leadFinderChurnkeyHash =
        action.payload.payload.leadFinderChurnkeyHash;
      state.inboxAnalyzerChurnkeyHash =
        action.payload.payload.inboxAnalyzerChurnkeyHash;
      state.paymentProvider = action.payload.payload.paymentProvider;
      state.featureUsages = action.payload.payload.featureUsages;
      state.permissions = action.payload.payload.permissions;
      state.resources = action.payload.payload.resources;
      state.adminDetails = action.payload.payload.adminDetails;
      state.signup_date = action.payload.payload.signup_date;
      state.icpStatus = action.payload.payload.icpStatus;
      state.createdAt = action.payload.payload.createdAt;
      state.isEligibleToExtendTrial =
        action.payload.payload.isEligibleToExtendTrial;
      state.allowInboxRotation = action.payload.payload.allowInboxRotation;
      state.userId = action.payload.payload.userId;
      state.companySize = action.payload.payload.companySize;
      state.planName = action.payload.payload.planName;
      state.industry = action.payload.payload.industry;
      state.country = action.payload.payload.country;
      state.phoneNumber = action.payload.payload.phoneNumber;
      state.jobRole = action.payload.payload.jobRole;
      state.shAccountId = action.payload.payload.shAccountId;
      state.useCase = action.payload.payload.useCase;
      state.verified = action.payload.payload.verified !== false;
      state.timeZone = action.payload.payload.timeZone;
      state.atmRole = action.payload.payload.atmRole;
      state.isAgencyUser = action.payload.payload.isAgencyUser;
      state.isMultichannelActive = action.payload.payload.isMultichannelActive;
      state.isSaleshandyCopilotEnabled =
        action.payload.payload.isSaleshandyCopilotEnabled;
      state.leadFinderLimit = action.payload.payload.leadFinderLimit;
      state.isAgency = action.payload.payload.isAgency;
      state.isSelfSignup = action.payload.payload.isSelfSignup;
      state.hasDomains = action.payload.payload.hasDomains;
      state.hasExpiringOrders =
        action.payload.payload.hasExpiringOrders ?? false;
      state.hasDedicatedIps = action.payload.payload.hasDedicatedIps;
      state.intercomHash = action.payload.payload.intercomHash;
      state.ssoStatus = action.payload.payload.ssoStatus?.value;
      state.organizationId = action.payload.payload.organizationId;
      state.isFirstStepTextOnlyEmail =
        action.payload.payload.isFirstStepTextOnlyEmail;
      state.customTrackingDomainEnabled =
        action.payload.payload.customTrackingDomainEnabled;
      // AL-3690: default to false if BE doesn't return the field (older BE deploy)
      state.is1To1EmailInsightsEnabled =
        action.payload.payload.is1To1EmailInsightsEnabled ?? false;
      state.shouldDisplayMobileAppBanner =
        action.payload.payload.shouldDisplayMobileAppBanner;
      state.isDemoAccount = action.payload.payload.isDemoAccount;
      state.creditGiven = action.payload.payload.creditGiven ?? false;
      state.isPlanUpgradeScheduled =
        action.payload.payload.isPlanUpgradeScheduled;
      state.isLtdUser = action.payload.payload.isLtdUser;
      state.isProspectEnrichmentEnabled =
        action.payload.payload.isProspectEnrichmentEnabled;
      state.shouldShowChangePassword =
        action.payload.payload.shouldShowChangePassword;
      state.isProspectEnrollmentEnabled =
        action?.payload?.payload?.isProspectEnrollmentEnabled;
      state.isDialerLocalPresenceEnabled =
        action?.payload?.payload?.isDialerLocalPresenceEnabled;
      state.isDialerFeatureEnabled =
        action?.payload?.payload?.isDialerFeatureEnabled;
      state.defaultEmailRevealType =
        action?.payload?.payload?.defaultEmailRevealType || 'work_email';
      state.dataExportPermission =
        action?.payload?.payload?.dataExportPermission ?? 1;
      state.hasV2LeadFinderAccess =
        action.payload.payload?.leadFinder?.hasLeadFinderV2Access;
      const existingLeadFinderCredits = state.leadFinder?.credits;
      state.leadFinder = {
        hasLeadFinderV2Access:
          action.payload.payload?.leadFinder?.hasLeadFinderV2Access,
        version: action.payload.payload?.leadFinder?.version,
        isVersionLocked: action.payload.payload?.leadFinder?.isVersionLocked,
        isFilterRestrictionEnabled:
          action.payload.payload?.leadFinder?.isFilterRestrictionEnabled,
        isBucket1Frozen: action.payload.payload?.leadFinder?.isBucket1Frozen,
        restrictedV1Access:
          action.payload.payload?.leadFinder?.restrictedV1Access,
        csvEnrichment: action.payload.payload?.leadFinder?.csvEnrichment,
        credits:
          action.payload.payload?.leadFinder?.credits ??
          existingLeadFinderCredits,
      };
    });
    builder.addCase(getUserSettingsRequest.rejected, (state, action) => {
      state.getUserSettingsRequest.status = RequestStatus.Failed;
      state.getUserSettingsRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get first sequence
    builder.addCase(getFirstSequenceRequest.pending, (state) => {
      state.getFirstSequenceRequest.status = RequestStatus.Pending;
      state.getFirstSequenceRequest.error = null;
    });
    builder.addCase(getFirstSequenceRequest.fulfilled, (state, action) => {
      state.getFirstSequenceRequest.status = RequestStatus.Succeeded;
      state.getFirstSequenceRequest.message = action.payload.message;
      state.firstSequence = action.payload.payload;
    });
    builder.addCase(getFirstSequenceRequest.rejected, (state, action) => {
      state.getFirstSequenceRequest.status = RequestStatus.Failed;
      state.getFirstSequenceRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get Post Load Meta Request
    builder.addCase(getPostLoadMetaRequest.pending, (state) => {
      state.getPostLoadMetaRequest.status = RequestStatus.Pending;
      state.getPostLoadMetaRequest.error = null;
    });
    builder.addCase(getPostLoadMetaRequest.fulfilled, (state, action) => {
      state.getPostLoadMetaRequest.status = RequestStatus.Succeeded;
      state.getPostLoadMetaResponse = action.payload.payload;
      state.getPostLoadMetaRequest.message = action.payload.message;
    });
    builder.addCase(getPostLoadMetaRequest.rejected, (state, action) => {
      state.getPostLoadMetaRequest.status = RequestStatus.Failed;
      state.getPostLoadMetaRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get sample sequence
    builder.addCase(getSampleSequenceRequest.pending, (state) => {
      state.getSampleSequenceRequest.status = RequestStatus.Pending;
      state.getSampleSequenceRequest.error = null;
    });
    builder.addCase(getSampleSequenceRequest.fulfilled, (state, action) => {
      state.getSampleSequenceRequest.status = RequestStatus.Succeeded;
      state.getSampleSequenceRequest.message = action.payload.message;
      state.firstSequence = action.payload.payload;
    });
    builder.addCase(getSampleSequenceRequest.rejected, (state, action) => {
      state.getSampleSequenceRequest.status = RequestStatus.Failed;
      state.getSampleSequenceRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Update user settings
    builder.addCase(updateUserSettingsRequest.pending, (state) => {
      state.updateUserSettingsRequest.status = RequestStatus.Pending;
      state.updateUserSettingsRequest.error = null;
    });
    builder.addCase(updateUserSettingsRequest.fulfilled, (state, action) => {
      state.updateUserSettingsRequest.status = RequestStatus.Succeeded;
      state.updateUserSettingsRequest.message = action.payload.message;
    });
    builder.addCase(updateUserSettingsRequest.rejected, (state, action) => {
      state.updateUserSettingsRequest.status = RequestStatus.Failed;
      state.updateUserSettingsRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Upgrade Onboarding Step
    builder.addCase(upgradeOnboardingStepRequest.pending, (state) => {
      state.upgradeOnboardingStepRequest.status = RequestStatus.Pending;
      state.upgradeOnboardingStepRequest.error = null;
    });
    builder.addCase(upgradeOnboardingStepRequest.fulfilled, (state, action) => {
      state.upgradeOnboardingStepRequest.status = RequestStatus.Succeeded;
      state.upgradeOnboardingStepRequest.message = action.payload.message;
      state.meta = action.payload.payload.meta;
    });
    builder.addCase(upgradeOnboardingStepRequest.rejected, (state, action) => {
      state.upgradeOnboardingStepRequest.status = RequestStatus.Failed;
      state.upgradeOnboardingStepRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Update Steps Progress Status
    builder.addCase(updateProfileProgressRequest.pending, (state) => {
      state.updateProfileProgressRequest.status = RequestStatus.Pending;
      state.updateProfileProgressRequest.error = null;
    });
    builder.addCase(updateProfileProgressRequest.fulfilled, (state, action) => {
      state.updateProfileProgressRequest.status = RequestStatus.Succeeded;
      state.updateProfileProgressRequest.message = action.payload.message;
    });
    builder.addCase(updateProfileProgressRequest.rejected, (state, action) => {
      state.updateProfileProgressRequest.status = RequestStatus.Failed;
      state.updateProfileProgressRequest.error = action.payload;
    });

    // Compare Auth Tokens
    builder.addCase(compareAuthTokenRequest.pending, (state) => {
      state.compareAuthTokenRequest.status = RequestStatus.Pending;
      state.compareAuthTokenRequest.error = null;
    });
    builder.addCase(compareAuthTokenRequest.fulfilled, (state, action) => {
      state.compareAuthTokenRequest.status = RequestStatus.Succeeded;
      state.compareAuthTokenRequest.message = action.payload.message;
      state.application.loggedUserEmail = action.payload.payload.email;
      state.application.shouldLogoutUser =
        action.payload.payload.shouldLogoutUser;
    });
    builder.addCase(compareAuthTokenRequest.rejected, (state, action) => {
      state.compareAuthTokenRequest.status = RequestStatus.Failed;
      state.compareAuthTokenRequest.error = action.payload;
    });

    // Create Sequence Status
    builder.addCase(
      createSequenceFromGettingStartedRequest.pending,
      (state) => {
        state.createSequenceFromGettingStartedRequest.status =
          RequestStatus.Pending;
        state.createSequenceFromGettingStartedRequest.error = null;
      },
    );
    builder.addCase(
      createSequenceFromGettingStartedRequest.fulfilled,
      (state, action) => {
        state.createSequenceFromGettingStartedRequest.status =
          RequestStatus.Succeeded;
        state.createSequenceFromGettingStartedRequest.message =
          action.payload.message;
        state.gettingStatedCreatedSequenceId = action.payload.payload.id;
      },
    );
    builder.addCase(
      createSequenceFromGettingStartedRequest.rejected,
      (state, action) => {
        state.createSequenceFromGettingStartedRequest.status =
          RequestStatus.Failed;
        state.createSequenceFromGettingStartedRequest.error = action.payload;
      },
    );

    // User notifications
    builder.addCase(getUserNotificationsRequest.rejected, (state) => {
      state.getUserNotificationsRequest.status = RequestStatus.Failed;
    });
    builder.addCase(getUserNotificationsRequest.pending, (state) => {
      state.getUserNotificationsRequest.status = RequestStatus.Pending;
    });
    builder.addCase(getUserNotificationsRequest.fulfilled, (state, action) => {
      state.notifications = [
        ...state.notifications,
        ...action.payload.payload.notifications,
      ];
      state.notificationCount = action.payload.payload.count;
      state.getUserNotificationsRequest.status = RequestStatus.Succeeded;
    });

    // Polling notifications
    builder.addCase(getPollingNotificationsRequest.rejected, (state) => {
      state.getPollingNotificationsRequest.status = RequestStatus.Failed;
    });
    builder.addCase(getPollingNotificationsRequest.pending, (state) => {
      state.getPollingNotificationsRequest.status = RequestStatus.Pending;
    });
    builder.addCase(
      getPollingNotificationsRequest.fulfilled,
      (state, action) => {
        const prevNotifications =
          state?.pollingNotifications?.notifications || [];
        const newNotifications = action.payload?.payload?.notifications;
        const shouldPoll = action.payload?.payload?.shouldPoll;

        const hasCompletedNotifications = newNotifications?.some((newNotif) => {
          if (prevNotifications?.length === 0) {
            return newNotif?.completedAt;
          }

          // Find matching notification from previous state
          const prevNotif = prevNotifications?.find(
            // eslint-disable-next-line no-underscore-dangle
            (prev) => prev?._id === newNotif?._id,
          );

          return (
            (!prevNotif && newNotif?.completedAt) ||
            (prevNotif && prevNotif?.completedAt !== newNotif?.completedAt)
          );
        });

        if (hasCompletedNotifications) {
          state.initiateFetchUnreadCount = true;
        }

        state.pollingNotifications = {
          shouldPoll,
          notifications: newNotifications,
        };
        state.getPollingNotificationsRequest.status = RequestStatus.Succeeded;
      },
    );

    // Update hidden notification
    builder.addCase(updateHiddenNotificationRequest.pending, (state) => {
      state.updateHiddenNotificationRequest.status = RequestStatus.Pending;
    });

    builder.addCase(updateHiddenNotificationRequest.fulfilled, (state) => {
      state.updateHiddenNotificationRequest.status = RequestStatus.Succeeded;
    });

    builder.addCase(updateHiddenNotificationRequest.rejected, (state) => {
      state.updateHiddenNotificationRequest.status = RequestStatus.Failed;
    });

    // Explore Demo Account Request
    builder.addCase(exploreDemoAccountRequest.pending, (state) => {
      state.exploreDemoAccountRequest.status = RequestStatus.Pending;
    });

    builder.addCase(exploreDemoAccountRequest.fulfilled, (state, action) => {
      state.exploreDemoAccountRequest.status = RequestStatus.Succeeded;
      state.demoAccountAccessToken = action.payload.payload.accessToken;
      // state.isDemoAccount = true;
    });

    builder.addCase(exploreDemoAccountRequest.rejected, (state) => {
      state.exploreDemoAccountRequest.status = RequestStatus.Failed;
    });

    // Marking notifications as read
    builder.addCase(markNotificationsAsReadRequest.fulfilled, (state) => {
      state.markNotificationsAsReadRequest.status = RequestStatus.Succeeded;
    });

    builder.addCase(markNotificationsAsReadRequest.rejected, (state) => {
      state.markNotificationsAsReadRequest.status = RequestStatus.Failed;
    });

    // Get user unread notification
    builder.addCase(
      getUserUnreadNotificationsRequest.fulfilled,
      (state, action) => {
        state.getUserUnreadNotificationsRequest.status =
          RequestStatus.Succeeded;
        state.unreadNotificationCount =
          action.payload.payload?.unReadNotificationCount || 0;
      },
    );

    builder.addCase(getUserUnreadNotificationsRequest.rejected, (state) => {
      state.getUserUnreadNotificationsRequest.status = RequestStatus.Failed;
    });

    // Get Unread Email Threads Count
    builder.addCase(
      getUnreadEmailThreadsCountRequest.fulfilled,
      (state, action) => {
        state.getUnreadEmailThreadsCountRequest.status =
          RequestStatus.Succeeded;
        state.unreadEmailThreadsCount = action.payload.payload.count || 0;
        state.filteredUnreadEmailThreadsCount =
          action.payload.payload?.filterCount;
      },
    );

    builder.addCase(getUnreadEmailThreadsCountRequest.rejected, (state) => {
      state.getUnreadEmailThreadsCountRequest.status = RequestStatus.Failed;
    });

    // Extend Trial Period
    builder.addCase(extendTrialPeriodRequest.pending, (state) => {
      state.extendTrialPeriodRequest.status = RequestStatus.Pending;
    });
    builder.addCase(extendTrialPeriodRequest.fulfilled, (state) => {
      state.extendTrialPeriodRequest.status = RequestStatus.Succeeded;
    });

    builder.addCase(extendTrialPeriodRequest.rejected, (state, action) => {
      state.extendTrialPeriodRequest.status = RequestStatus.Failed;
      state.extendTrialPeriodRequest.error = action.payload;
    });

    builder.addCase(getCoreFeaturesCreditsRequest.rejected, (state) => {
      state.getCoreFeaturesCreditsRequest.status = RequestStatus.Failed;
    });

    builder.addCase(getCoreFeaturesCreditsRequest.pending, (state) => {
      state.getCoreFeaturesCreditsRequest.status = RequestStatus.Pending;
    });

    builder.addCase(
      getCoreFeaturesCreditsRequest.fulfilled,
      (state, action) => {
        state.credits = action.payload.payload; // Assuming 'credits' is the returned data
        state.getCoreFeaturesCreditsRequest.status = RequestStatus.Succeeded;
      },
    );

    // Update User Feature Engagement Request
    builder.addCase(updateUserFeatureEngagementRequest.pending, (state) => {
      state.updateUserFeatureEngagementRequest.status = RequestStatus.Pending;
    });
    builder.addCase(
      updateUserFeatureEngagementRequest.fulfilled,
      (state, action) => {
        state.updateUserFeatureEngagementRequest.status =
          RequestStatus.Succeeded;
        state.updateUserFeatureEngagementResponse = action.payload.payload;
      },
    );
    builder.addCase(
      updateUserFeatureEngagementRequest.rejected,
      (state, action) => {
        state.updateUserFeatureEngagementRequest.status = RequestStatus.Failed;
        state.updateUserFeatureEngagementRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Update Lead Finder Version
    builder.addCase(updateLeadFinderVersionRequest.pending, (state) => {
      state.updateLeadFinderVersionRequest.status = RequestStatus.Pending;
      state.updateLeadFinderVersionRequest.error = null;
    });
    builder.addCase(
      updateLeadFinderVersionRequest.fulfilled,
      (state, action) => {
        state.updateLeadFinderVersionRequest.status = RequestStatus.Succeeded;
        state.updateLeadFinderVersionRequest.message = action.payload.message;
        if (action.payload.payload) {
          state.hasV2LeadFinderAccess =
            action.payload.payload?.hasLeadFinderV2Access;
          state.leadFinder = {
            ...state.leadFinder,
            hasLeadFinderV2Access:
              action.payload.payload?.hasLeadFinderV2Access,
            version: action.payload.payload?.version,
            isVersionLocked: action.payload.payload?.isVersionLocked,
          };
        }
      },
    );
    builder.addCase(
      updateLeadFinderVersionRequest.rejected,
      (state, action) => {
        state.updateLeadFinderVersionRequest.status = RequestStatus.Failed;
        state.updateLeadFinderVersionRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );
  },
});

// email | web-app

export {
  getUserSettingsRequest,
  updateUserSettingsRequest,
  upgradeOnboardingStepRequest,
  updateProfileProgressRequest,
  getSampleSequenceRequest,
  getCoreFeaturesCreditsRequest,
};
export const {
  updateIsPlanUpgradeScheduled,
  markNotificationAsRead,
  updateApplicationState,
  updateIsFirstStepTextOnlyEmail,
  setDefaultEmailRevealType,
  setCustomTrackingDomainEnabled,
  updateSkipOnboardingValue,
  updateShowSidebarValue,
  showGettingStartedVideoModal,
  resetFirstSequenceData,
  resetCompareAuthTokenRequest,
  resetGetUserSubscriptionMetadataRequest,
  resetCoreFeaturesCreditsRequestStatus,
  resetApplicationState,
  resetNotificationData,
  onSettingsTabProfileUpdate,
  updateIsMultichannelActive,
  setIsDialerLocalPresenceEnabled,
  enableBulkActionRequestPolling,
  disableBulkActionRequestPolling,
  setFirebaseProgressNotification,
  clearFirebaseProgressNotification,
  startNotificationBlink,
  stopNotificationBlink,
  resetInitiateFetchUnreadCount,
  triggerFetchUnreadCount,
  updateShowMobileApplicationBannerInMeta,
  deductAiVariantCredit,
  resetExploreDemoAccountRequest,
  setDemoAccountEntryPoint,
  updateLeadFinder,
  resetUpdateLeadFinderVersionRequest,
} = homeSlice.actions;
export default homeSlice.reducer;

export const selectPaddleCustomerId = (state: { home: any }) =>
  state.home?.subscription?.paddleCustomerId ||
  state.home?.leadFinderSubscription?.paddleCustomerId ||
  state.home?.inboxAnalyzerSubscription?.paddleCustomerId ||
  state.home?.dialerSubscription?.paddleCustomerId;
